import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { RunNode, RunSummaryRow } from "../../api/ParsersPanel";
import KPICard from "./components/KPICard";
import { useRunStats } from "./hooks/useRunStats";
interface RunsTabProps {
  node: RunNode;
}
type SortKey =
  | "collector_name"
  | "last_started_at"
  | "last_success_at"
  | "runs"
  | "failures"
  | "consecutive_failures"
  | "consecutive_empty_runs";
const RunsTab: React.FC<RunsTabProps> = ({ node }) => {
  const { data, loading, error } = useRunStats(node);
  const [sortKey, setSortKey] = useState<SortKey>("consecutive_failures");
  const [sortAsc, setSortAsc] = useState(false);
  const totals = useMemo(() => {
    const rows = data?.rows ?? [];
    const stuck = rows.filter((row) => row.stale_unfinished).length;
    const failing = rows.filter(
      (row) => !row.stale_unfinished && row.consecutive_failures > 0
    ).length;
    const healthy = rows.length - stuck - failing;
    const emptyRunStreaks = rows.filter(
      (row) => (row.consecutive_empty_runs ?? 0) > 0
    ).length;
    return {
      collectors: rows.length,
      stuck,
      failing,
      healthy,
      emptyRunStreaks,
    };
  }, [data]);
  const sorted = useMemo(() => {
    if (!data) return [];
    return [...data.rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (
        sortKey === "runs" ||
        sortKey === "failures" ||
        sortKey === "consecutive_failures" ||
        sortKey === "consecutive_empty_runs"
      ) {
        const aNumber = typeof av === "number" ? av : -1;
        const bNumber = typeof bv === "number" ? bv : -1;
        if (aNumber < bNumber) return sortAsc ? -1 : 1;
        if (aNumber > bNumber) return sortAsc ? 1 : -1;
        return 0;
      }
      const aString = av ?? "";
      const bString = bv ?? "";
      if (aString < bString) return sortAsc ? -1 : 1;
      if (aString > bString) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [data, sortKey, sortAsc]);
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc((a) => !a);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };
  const sortIndicator = (key: SortKey) =>
    sortKey === key ? (sortAsc ? " ▲" : " ▼") : "";
  const getStatus = (
    row: RunSummaryRow
  ): { label: string; bg: string; color: string } => {
    if (row.stale_unfinished) {
      return { label: "Stuck", bg: "#ffebee", color: "#c62828" };
    }
    if (row.last_success === false) {
      return { label: "Failing", bg: "#fff3e0", color: "#e65100" };
    }
    if (row.last_success === null) {
      return { label: "Running", bg: "#eeeeee", color: "#616161" };
    }
    return { label: "OK", bg: "#e8f5e9", color: "#2e7d32" };
  };
  const truncateError = (message: string | null) => {
    if (!message) return "-";
    return message.length > 80 ? `${message.slice(0, 80)}…` : message;
  };
  return (
    <Container>
      {data && (
        <>
          <KPICards>
            <KPICard label="Collectors" value={totals.collectors} />
            <KPICard
              label="Stuck"
              value={totals.stuck}
              accentSilent
            />
            <KPICard
              label="Failing"
              value={totals.failing}
              accentSilent
            />
            <KPICard
              label="Healthy"
              value={totals.healthy}
              accentHealthy
            />
            {node !== "arabic" && (
              <KPICard
                label="Empty-Run Streaks"
                value={totals.emptyRunStreaks}
                accent
              />
            )}
          </KPICards>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th $sortable onClick={() => toggleSort("collector_name")}>
                    Collector{sortIndicator("collector_name")}
                  </Th>
                  <Th $sortable onClick={() => toggleSort("last_started_at")}>
                    Last Run{sortIndicator("last_started_at")}
                  </Th>
                  <Th $sortable onClick={() => toggleSort("last_success_at")}>
                    Last Success{sortIndicator("last_success_at")}
                  </Th>
                  <Th $sortable align="right" onClick={() => toggleSort("runs")}>
                    Runs{sortIndicator("runs")}
                  </Th>
                  <Th $sortable align="right" onClick={() => toggleSort("failures")}>
                    Failures{sortIndicator("failures")}
                  </Th>
                  <Th $sortable
                    align="right"
                    onClick={() => toggleSort("consecutive_failures")}
                  >
                    Consecutive Failures
                    {sortIndicator("consecutive_failures")}
                  </Th>
                  <Th $sortable
                    align="right"
                    onClick={() => toggleSort("consecutive_empty_runs")}
                  >
                    Empty Streak{sortIndicator("consecutive_empty_runs")}
                  </Th>
                  <Th>Status</Th>
                  <Th>Last Error</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr>
                    <Td colSpan={9}>
                      <Msg>No runs found.</Msg>
                    </Td>
                  </tr>
                ) : (
                  sorted.map((row) => {
                    const status = getStatus(row);
                    return (
                      <Tr key={row.collector_name}>
                        <Td>{row.collector_name}</Td>
                        <Td>
                          {row.last_started_at
                            ? new Date(row.last_started_at).toLocaleString()
                            : "-"}
                        </Td>
                        <Td>
                          {row.last_success_at
                            ? new Date(row.last_success_at).toLocaleString()
                            : "-"}
                        </Td>
                        <Td align="right">{row.runs.toLocaleString()}</Td>
                        <Td align="right">{row.failures.toLocaleString()}</Td>
                        <Td align="right">
                          <FailureCount $alert={row.consecutive_failures >= 3}>
                            {row.consecutive_failures.toLocaleString()}
                          </FailureCount>
                        </Td>
                        <Td align="right">
                          {row.consecutive_empty_runs === null
                            ? "-"
                            : row.consecutive_empty_runs.toLocaleString()}
                        </Td>
                        <Td>
                          <Badge $bg={status.bg} $color={status.color}>
                            {status.label}
                          </Badge>
                        </Td>
                        <Td>{truncateError(row.last_error_message)}</Td>
                      </Tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </TableWrapper>
          <CardList>
            {sorted.length === 0 ? (
              <Msg>No runs found.</Msg>
            ) : (
              sorted.map((row) => {
                const status = getStatus(row);
                return (
                  <Card key={row.collector_name}>
                    <CardHeader>
                      <CollectorName>{row.collector_name}</CollectorName>
                      <Badge $bg={status.bg} $color={status.color}>
                        {status.label}
                      </Badge>
                    </CardHeader>
                    <CardGrid>
                      <CardCell>
                        <CellLabel>Last Run</CellLabel>
                        <CellValue>
                          {row.last_started_at
                            ? new Date(row.last_started_at).toLocaleString()
                            : "-"}
                        </CellValue>
                      </CardCell>
                      <CardCell>
                        <CellLabel>Last Success</CellLabel>
                        <CellValue>
                          {row.last_success_at
                            ? new Date(row.last_success_at).toLocaleString()
                            : "-"}
                        </CellValue>
                      </CardCell>
                      <CardCell>
                        <CellLabel>Runs</CellLabel>
                        <CellValue>{row.runs.toLocaleString()}</CellValue>
                      </CardCell>
                      <CardCell>
                        <CellLabel>Failures</CellLabel>
                        <CellValue>{row.failures.toLocaleString()}</CellValue>
                      </CardCell>
                      <CardCell>
                        <CellLabel>Consecutive Failures</CellLabel>
                        <CellValue>
                          <FailureCount $alert={row.consecutive_failures >= 3}>
                            {row.consecutive_failures.toLocaleString()}
                          </FailureCount>
                        </CellValue>
                      </CardCell>
                      <CardCell>
                        <CellLabel>Empty Streak</CellLabel>
                        <CellValue>
                          {row.consecutive_empty_runs === null
                            ? "-"
                            : row.consecutive_empty_runs.toLocaleString()}
                        </CellValue>
                      </CardCell>
                      <CardCell>
                        <CellLabel>Last Error</CellLabel>
                        <CellValue>{truncateError(row.last_error_message)}</CellValue>
                      </CardCell>
                    </CardGrid>
                  </Card>
                );
              })
            )}
          </CardList>
        </>
      )}
      {loading && <Msg>Loading…</Msg>}
      {error && <ErrorMsg>{error}</ErrorMsg>}
    </Container>
  );
};
export default RunsTab;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
const KPICards = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  @media (max-width: 480px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    width: 100%;
  }
`;
const Msg = styled.p`
  color: #aaa;
  font-size: 14px;
  padding: 8px 0;
`;
const ErrorMsg = styled.p`
  color: #c62828;
  font-size: 14px;
`;
const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
  @media (max-width: 768px) {
    display: none;
  }
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #fff;
  min-width: 750px;
`;
const Th = styled.th<{ align?: string; $sortable?: boolean }>`
  background: #0058A9;
  color: #fff;
  text-align: ${({ align }) => align ?? "left"};
  padding: 10px 14px;
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  cursor: ${({ $sortable }) => ($sortable ? "pointer" : "default")};
  user-select: none;
  &:hover {
    background: ${({ $sortable }) => ($sortable ? "#004a90" : "#0058A9")};
  }
`;
const Tr = styled.tr`
  border-bottom: 1px solid #f0f0f0;
  &:hover {
    background: #f6f9ff;
  }
`;
const Td = styled.td<{ align?: string }>`
  padding: 9px 14px;
  text-align: ${({ align }) => align ?? "left"};
  vertical-align: middle;
`;
const Badge = styled.span<{ $bg: string; $color: string }>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  white-space: nowrap;
`;
const FailureCount = styled.span<{ $alert: boolean }>`
  color: ${({ $alert }) => ($alert ? "#c62828" : "inherit")};
  font-weight: ${({ $alert }) => ($alert ? "700" : "inherit")};
`;
const CardList = styled.div`
  display: none;
  flex-direction: column;
  gap: 12px;
  @media (max-width: 768px) {
    display: flex;
  }
`;
const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
  padding: 14px;
`;
const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 10px;
`;
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 14px;
`;
const CardCell = styled.div`
  display: flex;
  flex-direction: column;
`;
const CellLabel = styled.span`
  font-size: 10px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;
const CellValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #222;
  word-break: break-word;
`;
const CollectorName = styled.span`
  font-size: 12px;
  color: #0058A9;
  word-break: break-all;
`;
